import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useLayout } from "../../context/LayoutContext";
import { useGetHelpCenterQuery } from "../../redux/hrms/helpCenterApi";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Book,
  Briefcase,
  Crown,
  Flag,
  HelpCircle,
  LifeBuoy,
  Loader,
  SlidersHorizontal,
  Shield,
  User,
  Users,
  ChevronRight,
} from "lucide-react";

const iconMap = {
  flag: Flag,
  user: User,
  users: Users,
  shield: Shield,
  briefcase: Briefcase,
  crown: Crown,
  book: Book,
  life: LifeBuoy,
  sliders: SlidersHorizontal,
};

/**
 * Help-center navigation rendered inside the main app sidebar.
 *
 * Tree shape (from the help-center API):
 *   group -> (items | modules[ -> items])
 * Each leaf article links to `/app/help-center/<id>`.
 *
 * Works in both the expanded sidebar (collapsible groups/modules) and the
 * collapsed sidebar (icon buttons with dropdowns).
 */
export function SidebarHelpNav({ expanded }) {
  const { data, isLoading } = useGetHelpCenterQuery();
  const { setSidebarExpanded, isSidebarExpanded } = useLayout();

  // On small screens start with the icon rail so content is not covered.
  useEffect(() => {
    if (window.innerWidth < 768 && isSidebarExpanded) {
      setSidebarExpanded(false);
    }
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the sidebar on mobile after navigating to an article.
  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      setSidebarExpanded(false);
    }
  };

  const groups = data?.groups || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link href="/app/help-center" onClick={handleNavClick}>
              <Button
                variant="ghost"
                className={cn(
                  "w-full h-10 items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50",
                  expanded ? "justify-start px-3" : "justify-center p-0"
                )}
              >
                <HelpCircle className="h-4 w-4" />
                {expanded && (
                  <span className="text-[clamp(10px,1.5vw,13px)]">Help Center</span>
                )}
              </Button>
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="font-medium z-50">
            Help Center
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <>
      {groups.map((group) => (
        <HelpGroup
          key={group.label}
          group={group}
          expanded={expanded}
          onNavClick={handleNavClick}
        />
      ))}
    </>
  );
}

function HelpGroup({ group, expanded, onNavClick }) {
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const Icon = iconMap[group.icon] || Book;
  const items = group.items || [];
  const modules = group.modules || [];

  // Auto-open the group when it contains the currently active article.
  const containsActive = [
    ...(items || []).map((i) => i.id),
    ...(modules || []).flatMap((m) => (m.items || []).map((i) => i.id)),
  ].some((id) => location === `/app/help-center/${id}`);

  useEffect(() => {
    if (containsActive) setIsOpen(true);
  }, [containsActive]);

  if (!expanded) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full h-10 p-0 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  <Icon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="start" className="w-60 max-h-[80vh] overflow-y-auto">
                <div className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border mb-1">
                  {group.label}
                </div>
                {items.map((item) => (
                  <HelpMenuItem key={item.id} id={item.id} title={item.title} onNavClick={onNavClick} />
                ))}
                {modules.map((module) => (
                  <div key={module.name}>
                    <div className="px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {module.name}
                    </div>
                    {module.items.map((item) => (
                      <HelpMenuItem key={item.id} id={item.id} title={item.title} onNavClick={onNavClick} />
                    ))}
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="font-medium z-50">
            {group.label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-between h-10 px-3 mb-1 font-normal cursor-pointer",
            isOpen
              ? "text-primary bg-primary/5"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
          )}
        >
          <div className="flex items-center gap-3 min-w-0">
            <Icon className="h-4 w-4 shrink-0" />
            <span className="text-[clamp(10px,1.5vw,13px)] truncate">
              {group.label}
            </span>
          </div>
          <ChevronRight
            className={cn("h-3 w-3 shrink-0 transition-transform", isOpen && "rotate-90")}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-4 space-y-1 animate-slide-down">
        <div className="border-l border-border pl-2 ml-2 mt-1 space-y-1">
          {items.map((item) => (
            <HelpLink key={item.id} id={item.id} title={item.title} onNavClick={onNavClick} />
          ))}
          {modules.map((module) => (
            <HelpModule key={module.name} module={module} onNavClick={onNavClick} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function HelpModule({ module, onNavClick }) {
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Auto-open the module when it contains the currently active article.
  const containsActive = (module.items || []).some(
    (i) => location === `/app/help-center/${i.id}`
  );

  useEffect(() => {
    if (containsActive) setIsOpen(true);
  }, [containsActive]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-between h-8 px-3 text-sm font-normal cursor-pointer",
            isOpen
              ? "text-primary bg-primary/5"
              : "text-muted-foreground hover:text-primary hover:bg-primary/5"
          )}
        >
          <span className="text-[clamp(10px,1.5vw,13px)] font-medium truncate">
            {module.name}
          </span>
          <ChevronRight
            className={cn("h-3 w-3 shrink-0 transition-transform", isOpen && "rotate-90")}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-3 space-y-1 animate-slide-down">
        {module.items.map((item) => (
          <HelpLink key={item.id} id={item.id} title={item.title} onNavClick={onNavClick} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function HelpLink({ id, title, onNavClick }) {
  const [location] = useLocation();
  const href = `/app/help-center/${id}`;
  const isActive = location === href;

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link href={href} onClick={onNavClick}>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start h-8 px-3 text-sm font-normal",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/5"
              )}
            >
              <span className="truncate text-left">{title}</span>
            </Button>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={10} className="font-medium z-50">
          {title}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function HelpMenuItem({ id, title, onNavClick }) {
  const [location] = useLocation();
  const href = `/app/help-center/${id}`;
  const isActive = location === href;

  return (
    <DropdownMenuItem asChild>
      <Link
        href={href}
        onClick={onNavClick}
        className={cn(
          "cursor-pointer",
          isActive && "text-primary font-medium"
        )}
      >
        {title}
      </Link>
    </DropdownMenuItem>
  );
}